import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeftIcon, LockClosedIcon,
  VideoCameraIcon, FunnelIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import { useNode } from '../contexts/NodeContext';
import { getNodeModelAssignments, bulkSaveNodeModelAssignments, getNodeCameras, getNodeGroups } from '../services/api.service';
import { useAuth } from '../contexts/AuthContext';
import {
  PageTitle, PageWithFilters, FilterColumn, FilterSection, FilterSearch, useFilterColumn,
  FillHeightCard, TABLE_HEAD, TABLE_TH, TABLE_ROW, tableHeadRow,
  ListPager, Callout, BTN_PRIMARY_SM, BTN_SECONDARY_SM, LoadingSpinner,
} from '@trinetra/shared';

interface Model { id: string; name: string; description?: string; }
interface Group { id: string; name: string; }
interface Camera { id: string; name: string; ip_address?: string; location?: string; groups?: any[]; }

const PAGE_SIZE = 30;

export default function ModelAssignmentPage() {
  const { selectedNode } = useNode();
  const nodeId = selectedNode?.nodeId;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [models, setModels] = useState<Model[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [cameraMemberships, setCameraMemberships] = useState<any[]>([]);
  const [serverGroupAssignments, setServerGroupAssignments] = useState<any[]>([]);
  const [serverCameraAssignments, setServerCameraAssignments] = useState<any[]>([]);

  const [groupAssignments, setGroupAssignments] = useState<Record<string, Set<string>>>({});
  const [cameraAssignments, setCameraAssignments] = useState<Record<string, Set<string>>>({});

  const [selectedGroup, setSelectedGroup] = useState('all');
  // The group panel is a LIST first — long enough on a real site to need its own filter,
  // independent of the camera search on the right.
  const [groupQuery, setGroupQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [page, setPage] = useState(1);

  const { hasPermission } = useAuth();
  const canAttach = hasPermission('ai-models.assign');

  const fetchData = useCallback(async () => {
    if (!nodeId) return;
    setLoading(true);
    setError('');
    try {
      const [assignData, camerasData, groupsData] = await Promise.all([
        getNodeModelAssignments(nodeId),
        getNodeCameras(nodeId),
        getNodeGroups(nodeId),
      ]);

      const allModels = assignData.models || [];
      const gAssign = assignData.group_assignments || [];
      const cAssign = assignData.camera_assignments || [];
      const memberships = assignData.camera_group_memberships || [];

      setModels(allModels);
      setServerGroupAssignments(gAssign);
      setServerCameraAssignments(cAssign);
      setCameraMemberships(memberships);

      const cams = Array.isArray(camerasData) ? camerasData : camerasData?.data || camerasData?.cameras || [];
      setCameras(cams);

      const grps = Array.isArray(groupsData) ? groupsData : groupsData?.data || [];
      setGroups(grps);

      const gMap: Record<string, Set<string>> = {};
      for (const a of gAssign) {
        if (!gMap[a.group_id]) gMap[a.group_id] = new Set();
        gMap[a.group_id].add(a.model_id);
      }
      setGroupAssignments(gMap);

      const cMap: Record<string, Set<string>> = {};
      for (const a of cAssign) {
        if (!cMap[a.camera_id]) cMap[a.camera_id] = new Set();
        cMap[a.camera_id].add(a.model_id);
      }
      setCameraAssignments(cMap);
      setIsDirty(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, [nodeId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /**
   * The group panel's selection is a group ID, so it means nothing on another site: left in
   * place it filters the camera list down to a group this site hasn't got, and the page opens
   * on "no cameras" for a site full of them. (The assignments themselves are replaced by the
   * fetch above, which also clears `isDirty`.)
   */
  const firstNodeRef = useRef(true);
  useEffect(() => {
    if (firstNodeRef.current) { firstNodeRef.current = false; return; }
    setSelectedGroup('all');
    setGroupQuery('');
    setSearchQuery('');
    setPage(1);
  }, [nodeId]);

  const cameraGroupMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    // Use memberships from API if available, fallback to cam.groups
    if (cameraMemberships.length > 0) {
      for (const m of cameraMemberships) {
        if (!map[m.camera_id]) map[m.camera_id] = [];
        map[m.camera_id].push(m.group_id);
      }
    } else {
      for (const cam of cameras) {
        map[cam.id] = cam.groups ? cam.groups.map((g: any) => g.id || g) : [];
      }
    }
    return map;
  }, [cameraMemberships, cameras]);

  const getInheritedModels = useCallback((cameraId: string) => {
    const cameraGroupIds = cameraGroupMap[cameraId] || [];
    const inherited = new Set<string>();
    for (const gid of cameraGroupIds) {
      const mods = groupAssignments[gid];
      if (mods) for (const mid of mods) inherited.add(mid);
    }
    return inherited;
  }, [cameraGroupMap, groupAssignments]);

  const toggleGroupModel = (groupId: string, modelId: string) => {
    setGroupAssignments(prev => {
      const next = { ...prev };
      next[groupId] = new Set(next[groupId] || []);
      if (next[groupId].has(modelId)) next[groupId].delete(modelId);
      else next[groupId].add(modelId);
      return next;
    });
    setIsDirty(true);
  };

  const toggleCameraModel = (cameraId: string, modelId: string) => {
    setCameraAssignments(prev => {
      const next = { ...prev };
      next[cameraId] = new Set(next[cameraId] || []);
      if (next[cameraId].has(modelId)) next[cameraId].delete(modelId);
      else next[cameraId].add(modelId);
      return next;
    });
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!nodeId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const groupPayload: any[] = [];
      for (const [groupId, modelIds] of Object.entries(groupAssignments)) {
        for (const modelId of modelIds) {
          groupPayload.push({ group_id: groupId, model_id: modelId, is_active: true });
        }
      }
      const cameraPayload: any[] = [];
      for (const [cameraId, modelIds] of Object.entries(cameraAssignments)) {
        for (const modelId of modelIds) {
          const inherited = getInheritedModels(cameraId);
          if (inherited.has(modelId)) continue;
          cameraPayload.push({ camera_id: cameraId, model_id: modelId, is_active: true });
        }
      }
      const result = await bulkSaveNodeModelAssignments(nodeId, {
        group_assignments: groupPayload,
        camera_assignments: cameraPayload,
        managed_group_ids: groups.map(g => g.id),
        managed_camera_ids: Object.keys(cameraAssignments),
      });
      setSuccess(`Saved! ${result.affected_cameras} camera(s) updated.`);
      setIsDirty(false);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    const gMap: Record<string, Set<string>> = {};
    for (const a of serverGroupAssignments) {
      if (!gMap[a.group_id]) gMap[a.group_id] = new Set();
      gMap[a.group_id].add(a.model_id);
    }
    setGroupAssignments(gMap);
    const cMap: Record<string, Set<string>> = {};
    for (const a of serverCameraAssignments) {
      if (!cMap[a.camera_id]) cMap[a.camera_id] = new Set();
      cMap[a.camera_id].add(a.model_id);
    }
    setCameraAssignments(cMap);
    setIsDirty(false);
  };

  const filteredCameras = useMemo(() => {
    let cams = cameras;
    if (selectedGroup !== 'all') {
      cams = cams.filter(c => (cameraGroupMap[c.id] || []).includes(selectedGroup));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      cams = cams.filter(c => (c.name || '').toLowerCase().includes(q) || (c.ip_address || '').includes(q) || (c.location || '').toLowerCase().includes(q));
    }
    return cams;
  }, [cameras, selectedGroup, searchQuery, cameraGroupMap]);

  const filteredGroups = useMemo(() => {
    const q = groupQuery.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => (g.name || '').toLowerCase().includes(q));
  }, [groups, groupQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredCameras.length / PAGE_SIZE);
  const paginatedCameras = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredCameras.slice(start, start + PAGE_SIZE);
  }, [filteredCameras, page]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [selectedGroup, searchQuery]);

  // The left column: the camera search, then the group list (a filter AND the group-level
  // assignment editor) — flush against the side nav, as on Search & Track.
  const filterCol = useFilterColumn('model-assignment');
  const activeFilters = (searchQuery ? 1 : 0) + (selectedGroup !== 'all' ? 1 : 0) + (groupQuery ? 1 : 0);
  const clearFilters = () => { setSearchQuery(''); setSelectedGroup('all'); setGroupQuery(''); };

  if (!nodeId) return <p className="py-16 text-center text-muted-foreground">Select a site first.</p>;

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <PageTitle
        title={(
          <>
            <Link to="/models" aria-label="Back to models" className="mr-1.5 inline-flex rounded p-0.5 align-[-3px] text-muted-foreground hover:bg-muted hover:text-foreground">
              <ArrowLeftIcon className="h-4 w-4" />
            </Link>
            Model Assignments
          </>
        )}
        subtitle="Assign AI models to camera groups and individual cameras"
      >
        {isDirty && (
          <button onClick={handleDiscard} className={BTN_SECONDARY_SM}>
            Discard
          </button>
        )}
        {canAttach && (
          <button data-tour="assign-save" onClick={handleSave} disabled={!isDirty || saving || !canAttach}
            className={BTN_PRIMARY_SM}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        )}
      </PageTitle>

      {/* The page body: PageWithFilters must stay a direct child of the shell's <main> (no
          wrapper) so it goes flush. Groups on the left, the camera table on the right. */}
      <PageWithFilters
        filters={(
          <FilterColumn
            width="wide"
            open={filterCol.open}
            onToggle={filterCol.toggle}
            label="Filters"
            activeCount={activeFilters}
            onClearAll={clearFilters}
          >
            <div data-tour="assign-camera-search">
              <FilterSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search camera name, IP, location…" />
            </div>

            <FilterSection
              title="Camera groups"
              hint={groups.length > 0 ? `${groups.length}` : null}
              action={selectedGroup !== 'all' ? (
                <button type="button" onClick={() => setSelectedGroup('all')} className="text-[11px] text-muted-foreground hover:text-foreground">
                  Show all
                </button>
              ) : null}
            >
              <div data-tour="assign-groups" className="space-y-2">
                <p className="text-[11px] text-muted-foreground">
                  A model set here applies to every camera in that group. Click a name to filter the camera list.
                </p>
                {(groups.length > 6 || groupQuery) && (
                  <FilterSearch value={groupQuery} onChange={setGroupQuery} placeholder="Search groups…" />
                )}
                <div className="space-y-1">
                  {groups.length === 0 ? (
                    <p className="text-[12px] text-muted-foreground py-6 text-center">No groups found</p>
                  ) : filteredGroups.length === 0 ? (
                    <div className="py-6 text-center">
                      <p className="text-[12px] text-muted-foreground">No group matches “{groupQuery}”</p>
                      <button onClick={() => setGroupQuery('')} className="mt-1 text-[11px] font-medium text-brand hover:underline">Clear search</button>
                    </div>
                  ) : (
                    filteredGroups.map(group => {
                      const groupModelSet = groupAssignments[group.id] || new Set();
                      const cameraCount = cameras.filter(c => (cameraGroupMap[c.id] || []).includes(group.id)).length;
                      const isActive = selectedGroup === group.id;
                      return (
                        <div
                          key={group.id}
                          className="rounded-r-lg p-3 transition-colors"
                          style={{
                            // Selected = the sidebar's active treatment (brand tint plus a 3px brand
                            // edge), the same signal the nav and the Camera Groups list use, so
                            // "which group am I filtering by" reads identically across the app. The
                            // transparent stripe on the others keeps the rows from shifting.
                            backgroundColor: isActive ? 'var(--sidebar-active-bg)' : undefined,
                            borderLeft: `3px solid ${isActive ? 'var(--sidebar-active-border)' : 'transparent'}`,
                          }}
                        >
                          <button
                            onClick={() => setSelectedGroup(isActive ? 'all' : group.id)}
                            aria-pressed={isActive}
                            title={isActive ? 'Showing only this group — click to show all cameras' : 'Show only this group\u2019s cameras'}
                            className="mb-2 flex w-full items-center gap-1.5 text-left"
                          >
                            {isActive && <FunnelIcon className="h-3 w-3 shrink-0 text-brand" />}
                            <span className={`min-w-0 truncate text-[12px] font-semibold ${isActive ? 'text-brand' : 'text-foreground'}`} title={group.name}>{group.name}</span>
                            <span className="ml-auto flex shrink-0 items-center gap-1">
                              {/* Two numbers an operator actually asks of a group row: how many
                                  models it pushes, and how many cameras that reaches. */}
                              <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                                groupModelSet.size
                                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                                  : 'bg-muted text-muted-foreground'
                              }`}>
                                {groupModelSet.size} model{groupModelSet.size === 1 ? '' : 's'}
                              </span>
                              <span
                                className="inline-flex items-center gap-0.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                                title={`${cameraCount} camera${cameraCount === 1 ? '' : 's'} in this group`}
                              >
                                <VideoCameraIcon className="h-3 w-3" />{cameraCount}
                              </span>
                            </span>
                          </button>
                          <div className="flex flex-wrap gap-1.5">
                            {models.map(model => {
                              const active = groupModelSet.has(model.id);
                              return (
                                <button key={model.id} onClick={() => toggleGroupModel(group.id, model.id)}
                                  disabled={!canAttach}
                                  className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-medium border transition-all disabled:opacity-60 disabled:cursor-not-allowed ${active
                                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                                    : 'border-border bg-background text-foreground hover:bg-muted'
                                  }`}
                                >
                                  <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-muted-foreground/50'}`} />
                                  {model.name}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </FilterSection>
          </FilterColumn>
        )}
      >
        {/* Messages */}
        {error && <Callout tone="danger">{error}</Callout>}
        {success && <Callout tone="success">{success}</Callout>}

        {/* How it works */}
        <div data-tour="assign-legend" className="rounded-xl border border-border bg-muted/30 p-3 text-[12px] text-muted-foreground space-y-1.5">
          <p>
            <span className="font-semibold text-foreground">Group assignment</span> (left column) applies a model to <span className="font-medium text-foreground">every camera</span> in that group. Those cameras show the model as{' '}
            <span className="inline-flex items-center gap-0.5"><LockClosedIcon className="h-3 w-3" /> inherited</span> and it can’t be toggled off per-camera — remove it from the group instead.
          </p>
          <p>
            <span className="font-semibold text-foreground">Individual assignment</span> (camera list) applies a model to that <span className="font-medium text-foreground">single camera only</span>, on top of anything inherited from its groups.
          </p>
          <p className="flex flex-wrap items-center gap-4 pt-1">
            <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Assigned</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" /> Not assigned</span>
            <span className="inline-flex items-center gap-1.5"><LockClosedIcon className="h-3 w-3" /> Inherited from group</span>
          </p>
        </div>

        {/* What the list shows — the group filter as a removable chip, above the results (a
            folded column must not hide why the list is short). */}
        <div className="flex flex-wrap items-center gap-2 px-1 text-[12px] text-muted-foreground">
          <span>
            {filteredCameras.length === cameras.length
              ? `${cameras.length} camera${cameras.length === 1 ? '' : 's'}`
              : `${filteredCameras.length} of ${cameras.length} cameras`}
          </span>
          {selectedGroup !== 'all' && (
            <button onClick={() => setSelectedGroup('all')}
              title="Clear the group filter"
              className="inline-flex min-w-0 items-center gap-1 rounded-full bg-brand/10 border border-brand/20 px-2 py-0.5 text-[11px] font-medium text-brand hover:bg-brand/20 transition-colors">
              <FunnelIcon className="h-3 w-3 shrink-0" />
              <span className="max-w-[12rem] truncate">{groups.find(g => g.id === selectedGroup)?.name || 'Group'}</span>
              <XMarkIcon className="h-3 w-3 shrink-0 text-brand/70" />
            </button>
          )}
        </div>

        {/* The cameras: ONE card filling the rest of the screen, rows scrolling under a sticky
            header. No filter strip above it inside the card, so the header row is white. */}
        <div data-tour="assign-cameras">
          <FillHeightCard>
            {paginatedCameras.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center py-16 text-center text-muted-foreground">
                <VideoCameraIcon className="mx-auto h-8 w-8 opacity-40" />
                <p className="mt-2 text-[14px]">
                  {searchQuery || selectedGroup !== 'all' ? 'No cameras match these filters' : 'No cameras found'}
                </p>
                {(searchQuery || selectedGroup !== 'all') && (
                  <button
                    onClick={() => { setSearchQuery(''); setSelectedGroup('all'); }}
                    className="mt-2 text-[12px] font-medium text-brand hover:underline"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <div className="min-h-0 flex-1 overflow-auto">
                <table className="w-full text-left">
                  <thead className={TABLE_HEAD}>
                    <tr className={tableHeadRow(false)}>
                      <th className={`${TABLE_TH} w-44 pl-4 sm:w-52`}>Camera</th>
                      <th className={`${TABLE_TH} pr-4`}>Models</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCameras.map(camera => {
                      const inherited = getInheritedModels(camera.id);
                      const directSet = cameraAssignments[camera.id] || new Set();
                      const cameraGroupIds = cameraGroupMap[camera.id] || [];
                      const cameraGroupNames = cameraGroupIds.map(gid => {
                        const g = groups.find(gr => gr.id === gid);
                        return g ? g.name : '';
                      }).filter(Boolean).join(', ');
                      return (
                        <tr key={camera.id} className={TABLE_ROW}>
                          <td className="py-2.5 pl-4 pr-3 align-middle">
                            <div className="w-36 sm:w-44">
                              <h4 className="text-[12px] font-medium text-foreground truncate" title={camera.name}>{camera.name}</h4>
                              <p
                                className="text-[10px] text-muted-foreground truncate mt-0.5"
                                title={cameraGroupNames ? `In: ${cameraGroupNames}` : camera.ip_address || undefined}
                              >
                                {cameraGroupNames || camera.ip_address || '—'}
                              </p>
                            </div>
                          </td>
                          <td className="py-2.5 pl-3 pr-4 align-middle">
                            <div className="flex flex-wrap gap-1.5">
                              {models.map(model => {
                                const isInherited = inherited.has(model.id);
                                const isChecked = isInherited || directSet.has(model.id);
                                return (
                                  <button key={model.id}
                                    onClick={() => !isInherited && toggleCameraModel(camera.id, model.id)}
                                    disabled={isInherited || !canAttach}
                                    title={isInherited ? 'Inherited from group' : model.name}
                                    className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-medium border transition-all ${
                                      isInherited
                                        ? 'border-border bg-muted/40 text-muted-foreground cursor-default'
                                        : isChecked
                                          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 cursor-pointer'
                                          : 'border-border bg-background text-foreground hover:bg-muted cursor-pointer'
                                    }`}
                                  >
                                    {isInherited && <LockClosedIcon className="h-2.5 w-2.5" />}
                                    {!isInherited && <span className={`h-1.5 w-1.5 rounded-full ${isChecked ? 'bg-emerald-500' : 'bg-muted-foreground/50'}`} />}
                                    {model.name}
                                  </button>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination — the card's footer */}
            {totalPages > 1 && (
              <ListPager
                bare
                className="shrink-0 border-t border-border px-4 py-2"
                page={page}
                pageSize={PAGE_SIZE}
                total={filteredCameras.length}
                onPageChange={setPage}
                itemLabel="cameras"
              />
            )}
          </FillHeightCard>
        </div>
      </PageWithFilters>

      {/* Dirty state bar */}
      {isDirty && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-card px-6 py-3 flex items-center justify-between shadow-lg">
          <span className="text-[13px] font-medium text-warning">⚠ You have unsaved changes</span>
          <div className="flex items-center gap-2">
            <button onClick={handleDiscard} className={BTN_SECONDARY_SM}>
              Discard
            </button>

            {canAttach && (
              <button onClick={handleSave} disabled={saving || !canAttach}
                className={BTN_PRIMARY_SM}>
                {saving ? 'Saving…' : 'Save'}
              </button>

            )}
          </div>
        </div>
      )}
    </>
  );
}
